import React, { useEffect, useState } from 'react';
import { MagneticButton } from '../common/MagneticButton';
import { DoubleBezelCard } from '../common/DoubleBezelCard';

export const HeroSection: React.FC = () => {
  const [metrics, setMetrics] = useState({
    carbonGt: 1.482,
    canopyMha: 84.62,
    biodiversityPct: 99.4,
    activeDrones: 14280,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics((prev) => ({
        carbonGt: +(prev.carbonGt + 0.0004).toFixed(4),
        canopyMha: +(prev.canopyMha + 0.002).toFixed(3),
        biodiversityPct: +Math.min(99.9, prev.biodiversityPct + 0.001).toFixed(2),
        activeDrones: prev.activeDrones + (Math.random() > 0.6 ? 1 : 0),
      }));
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-[100dvh] flex flex-col justify-center pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6 z-10"
    >
      <div className="max-w-7xl mx-auto w-full space-y-12 sm:space-y-16">
        {/* Archival Catalog Plaque */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-carbon-900/10 font-mono text-[11px] text-carbon-900/60">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-chlorophyll inline-block" />
            <span className="font-bold text-carbon-900">CATALOGUE // GAIA-2026-CODEX</span>
            <span className="text-carbon-900/30">|</span>
            <span>SECTOR: GLOBAL BIOMES</span>
          </div>
          <div className="flex items-center gap-4">
            <span>COORDINATES: -03°04' S // -60°00' W</span>
            <span className="hidden sm:inline text-chlorophyll-deep font-semibold">STATUS: CONTINUOUS RECOVERY</span>
          </div>
        </div>

        {/* Monumental Headline */}
        <div className="space-y-8 max-w-5xl">
          <h1 className="font-serif font-black text-5xl sm:text-7xl md:text-8xl lg:text-9xl tracking-tight text-carbon-900 leading-[0.92]">
            PLANETARY <br />
            <span className="italic font-normal text-chlorophyll-deep">EQUILIBRIUM.</span>
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 pt-2 items-start">
            <p className="md:col-span-8 text-base sm:text-xl text-carbon-900/80 font-sans font-normal leading-relaxed">
              Nature is not an aesthetic to be protected by paper pledges. It is a complex, active thermodynamic engine governed by physical mass balances. GAIA PULSE orchestrates autonomous reforestation swarms, deep-water alkalinity enhancement, and permanent subterranean basalt crystallization.
            </p>

            <div className="md:col-span-4 flex flex-col sm:flex-row md:flex-col gap-3 pt-1">
              <MagneticButton
                href="#pillars"
                variant="primary"
                size="lg"
                showArrow={true}
              >
                Inspect Biome Atlas
              </MagneticButton>

              <MagneticButton
                href="#simulator"
                variant="secondary"
                size="lg"
                showArrow={false}
              >
                Model Carbon Drawdown
              </MagneticButton>
            </div>
          </div>
        </div>

        {/* 4 Architectural Specimen Plates */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 pt-6">
          {/* Plate 1 */}
          <DoubleBezelCard enableTilt={true} glowColor="emerald" className="p-5 sm:p-6">
            <div className="font-mono text-[10px] text-chlorophyll-deep uppercase tracking-widest mb-1.5 flex items-center justify-between">
              <span>CO2 MINERALIZED</span>
              <span className="text-carbon-900/30">01</span>
            </div>
            <div className="text-3xl sm:text-4xl font-serif font-bold text-carbon-900 tracking-tight">
              {metrics.carbonGt.toFixed(3)} <span className="text-xs font-mono font-normal text-chlorophyll-deep">Gt</span>
            </div>
            <p className="text-[11px] text-carbon-900/60 mt-2 font-mono leading-tight">
              Irreversibly locked into basalt crystal.
            </p>
          </DoubleBezelCard>

          {/* Plate 2 */}
          <DoubleBezelCard enableTilt={true} glowColor="ozone" className="p-5 sm:p-6">
            <div className="font-mono text-[10px] text-ozone-deep uppercase tracking-widest mb-1.5 flex items-center justify-between">
              <span>CANOPY HEALED</span>
              <span className="text-carbon-900/30">02</span>
            </div>
            <div className="text-3xl sm:text-4xl font-serif font-bold text-carbon-900 tracking-tight">
              {metrics.canopyMha.toFixed(2)} <span className="text-xs font-mono font-normal text-ozone-deep">Mha</span>
            </div>
            <p className="text-[11px] text-carbon-900/60 mt-2 font-mono leading-tight">
              Multi-tiered native forest biomass.
            </p>
          </DoubleBezelCard>

          {/* Plate 3 */}
          <DoubleBezelCard enableTilt={true} glowColor="solar" className="p-5 sm:p-6">
            <div className="font-mono text-[10px] text-solar-amber uppercase tracking-widest mb-1.5 flex items-center justify-between">
              <span>BIODIVERSITY INDEX</span>
              <span className="text-carbon-900/30">03</span>
            </div>
            <div className="text-3xl sm:text-4xl font-serif font-bold text-carbon-900 tracking-tight">
              {metrics.biodiversityPct.toFixed(1)} <span className="text-xs font-mono font-normal text-solar-amber">%</span>
            </div>
            <p className="text-[11px] text-carbon-900/60 mt-2 font-mono leading-tight">
              Bio-acoustically verified species return.
            </p>
          </DoubleBezelCard>

          {/* Plate 4 */}
          <DoubleBezelCard enableTilt={true} glowColor="emerald" className="p-5 sm:p-6">
            <div className="font-mono text-[10px] text-chlorophyll-deep uppercase tracking-widest mb-1.5 flex items-center justify-between">
              <span>AUTONOMOUS SWARMS</span>
              <span className="text-carbon-900/30">04</span>
            </div>
            <div className="text-3xl sm:text-4xl font-serif font-bold text-carbon-900 tracking-tight">
              {metrics.activeDrones.toLocaleString()}
            </div>
            <p className="text-[11px] text-carbon-900/60 mt-2 font-mono leading-tight">
              Active high-cadence planting units.
            </p>
          </DoubleBezelCard>
        </div>
      </div>
    </section>
  );
};
